// The front yard's and the Podwórko's pictures with their painted sky cut out (scripts/yard_sky.py), one per look
// (time of day) and level, and where each one's horizon is: the shared live sky (farm/outdoorSky.js) is drawn
// behind them, down to that row. Spec docs/superpowers/specs/2026-11-02-house-yards-sky-design.md, step 1.
// Pure (node --test).
export const YARD_LOOKS = ['night', 'morning', 'day', 'evening'];

// The lowest source row with sky in that level's day picture (+1 px grow), from `yard_relight.py build`, 2026-11-03.
// The pictures are 941 rows high and drawn 720 high.
export const YARD_HORIZON_SRC_Y = {
  front: { 1: 609, 2: 609, 3: 608, 4: 563, 5: 512 },
  back: { 1: 386, 2: 386, 3: 386, 4: 386, 5: 386 },   // the far layer's hills (behind every level's near picture)
};
const SRC_H = 941;
const VIEW_H = 720;

// The Podwórko's far landscape (scripts/yard_relight.py): the hills, fields and tree line beyond the fence, painted on
// its own, one picture per look, behind every level's near picture (transparent above the fence but its hedge, posts,
// buildings and sapling). It slides at YARD_FAR_PARALLAX of the lawn's speed as you walk (owner 2026-11-02: "the
// mountains should move in respect to the distance"). Its bottom row (source px, hidden behind the lawn) and, per
// level, a vertical offset (none: the landscape is the same behind every level).
export const YARD_FAR_PARALLAX = 0.45;
// Its picture holds source rows top..bottom only (all sky above, the near picture opaque below: yard_relight.py).
export const YARD_FAR_SRC = { top: 300, bottom: 500, dy: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } };
export function yardFarKey(look) {
  return `bg_yard_back_far_${yardLookFor(look)}`;
}

// The sky's pan as you walk the Podwórko (it scrolls 640 px), 0 at the Podwórko's left edge, so the sky carries on from
// the front yard's (which doesn't scroll) across the join. The sky parts take their own share of it (near clouds 0.18,
// sun 0.05): at 0.5 the clouds slide ~9% of the walk and the sun ~2.5%, a far-away sky. The street's 2.2 had the
// clouds at 40%, which read as the sky rushing past (owner 2026-11-02).
export const YARD_SKY_PAN = 0.5;

const RECTS = {
  front: { x: 640, y: 360, w: 1280, h: 720 },
  back: { x: 960, y: 360, w: 1920, h: 720 },   // backyardLayout.js BACKYARD_BG_PLACEMENT
};

const levelOf = (level) => (Number.isInteger(level) && level >= 1 && level <= 5 ? level : 1);

export function yardLookFor(phase) {
  return YARD_LOOKS.includes(phase) ? phase : 'day';
}

export function yardKey(side, look, level) {
  return `bg_yard_${side}_${yardLookFor(look)}_lvl${levelOf(level)}`;
}

// The Podwórko's sky goes down to its far layer's hills: level 1's, moved with the level's rail.
export function yardHorizonY(side, level) {
  const src = side === 'back'
    ? YARD_HORIZON_SRC_Y.back[1] + YARD_FAR_SRC.dy[levelOf(level)]
    : YARD_HORIZON_SRC_Y[side][levelOf(level)];
  return (src * VIEW_H) / SRC_H;
}

// The pictures of a look, back to front. The Podwórko's far layer comes first, with its own scroll factor.
export function yardRects(side, look, level) {
  const near = { key: yardKey(side, look, level), ...RECTS[side], originX: 0.5, originY: 0.5 };
  if (side !== 'back') return [near];
  const far = {
    key: yardFarKey(look), x: 0, y: ((YARD_FAR_SRC.top + YARD_FAR_SRC.dy[levelOf(level)]) * VIEW_H) / SRC_H,
    w: RECTS.back.w, h: ((YARD_FAR_SRC.bottom - YARD_FAR_SRC.top) * VIEW_H) / SRC_H, originX: 0, originY: 0,
    scrollFactor: YARD_FAR_PARALLAX,
  };
  return [far, near];
}

export function yardSkyPanX(side, scrollX) {
  return side === 'back' ? (-scrollX * YARD_SKY_PAN || 0) : 0;   // never -0
}

// What to fetch after the current look: the next one first (the yard crossfades into it), then — not on phones —
// the other two in the order they come.
export function yardPrefetch(side, look, level, { lean = false } = {}) {
  const i = YARD_LOOKS.indexOf(yardLookFor(look));
  const next = [1, 2, 3].map((k) => YARD_LOOKS[(i + k) % YARD_LOOKS.length]);
  return (lean ? next.slice(0, 1) : next).flatMap((l) => yardRects(side, l, level).map((r) => r.key));
}

// The level to draw when the state says `wanted` and `drawn` is on screen: the new one once its picture is in
// (`ready(level)`), the old one until then — a level whose picture is still downloading would leave the yard empty.
export function levelToDraw(drawn, wanted, ready) {
  return wanted !== drawn && ready(wanted) ? wanted : drawn;
}

// The pictures a level needs before it may be drawn: the look on screen (a redraw draws it, even mid-fade) and the
// one the phase asks for, both at that level.
export function levelKeys(side, looks, level) {
  return [...new Set(looks.flatMap((l) => yardRects(side, l, level).map((r) => r.key)))];
}
